import { Box, Button, type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { Link as RouterLink } from "react-router-dom";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { REASSIST_LABELS } from "../config";
import { focusRingSx, rowHoverBg, tapExpandedSx } from "../utils/tokens";

/**
 * ボタンの高さ。
 * - page: 画面の中のボタン（PC 40px、SP 44px）
 * - card: マイページのカード（PC 44px、SP 48px）
 * - small: 行の中・再読み込み（見た目 32px、タップ領域は ::after で 44px 以上）
 */
export type ReassistButtonSize = "page" | "card" | "small";

interface Props {
  label: string;
  variant: "contained" | "outlined" | "text";
  /** 押したときの処理。to と同時には渡さない。 */
  onClick?: () => void;
  /** 画面内の行き先（React Router のリンクにする）。 */
  to?: string;
  startIcon?: React.ReactNode;
  size?: ReassistButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
}

const HEIGHT: Record<ReassistButtonSize, number | Record<string, number>> = {
  page: { xs: 44, [MOBILE_BREAKPOINT]: 40 },
  card: { xs: 48, [MOBILE_BREAKPOINT]: 44 },
  small: 32,
};

/**
 * R.E.ASSIST のボタン（方針書 2-9）。主（contained）、副（outlined）、text の3つの型だけを出す。
 * 文字 14px／700、字間 0.02em、アイコン 18px、文字との間 6px。影は出さない。
 * 角丸は theme（5px）に任せる。
 */
export const reassistButtonSx =
  (
    variant: Props["variant"],
    size: ReassistButtonSize
  ): ((theme: Theme) => SystemStyleObject<Theme>) =>
  (theme) => ({
    ...(size === "small" ? tapExpandedSx : {}),
    height: HEIGHT[size],
    minHeight: 0,
    minWidth: variant === "text" ? 44 : 0,
    px: variant === "text" ? 1 : 2.5,
    py: 0,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.02em",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    boxShadow: "none",
    transition: "background-color 0.15s, border-color 0.15s",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    "& .MuiButton-startIcon": { mr: "6px", ml: 0 },
    "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 18 },
    "&:hover, &:active": { boxShadow: "none" },
    ...focusRingSx(theme),
    ...(variant === "contained" && {
      color: theme.palette.white.main,
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        boxShadow: "none",
        backgroundColor: theme.palette.dark.main,
      },
    }),
    ...(variant === "outlined" && {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.white.main,
      border: `1px solid ${theme.palette.border.main}`,
      "&:hover": {
        backgroundColor: rowHoverBg(theme),
        borderColor: theme.palette.primary.main,
      },
    }),
    ...(variant === "text" && {
      // text は高さが小さくても押せる範囲を 44px 四方以上にする
      minHeight: 44,
      color: theme.palette.primary.main,
      "&:hover": { backgroundColor: rowHoverBg(theme) },
    }),
  });

/**
 * R.E.ASSIST画面のボタン。サイドバー、マイページ、/reassist、/reassist/archive で共通に使う。
 * fullWidth でないときは内容幅（fit-content）で置く。
 */
export const ReassistButton: React.FC<Props> = ({
  label,
  variant,
  onClick,
  to,
  startIcon,
  size = "page",
  fullWidth = false,
  disabled = false,
}) => {
  const common = {
    className: "TapExpanded",
    variant,
    startIcon,
    disabled,
    disableElevation: true,
    fullWidth,
    sx: reassistButtonSx(variant, size),
  } as const;
  const button =
    to !== undefined ? (
      <Button {...common} component={RouterLink} to={to} onClick={onClick}>
        {label}
      </Button>
    ) : (
      <Button {...common} onClick={onClick}>
        {label}
      </Button>
    );
  if (fullWidth) return button;
  return <Box sx={{ width: "fit-content", maxWidth: "100%" }}>{button}</Box>;
};

interface EntryProps {
  onClick?: () => void;
  to?: string;
  size?: ReassistButtonSize;
  fullWidth?: boolean;
  /** 既定は contained。1画面に contained を2つ置かないときは outlined を渡す。 */
  variant?: Props["variant"];
}

/** 主ボタン「役所資料を依頼する」（プラスアイコン）。閲覧専用ユーザーには押せない状態で出す（BE も 403 で拒否する）。 */
export const ReassistRequestButton: React.FC<EntryProps> = ({
  variant = "contained",
  ...rest
}) => {
  const isReadOnly = useReadOnlyUser();
  return (
    <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth={rest.fullWidth}>
      <ReassistButton
        label={REASSIST_LABELS.request}
        variant={variant}
        startIcon={<AddIcon />}
        disabled={isReadOnly}
        {...rest}
      />
    </ReadOnlyTooltip>
  );
};

/** 副ボタン「役所資料を探す」（検索アイコン）。リユース資料（黒塗り）を探して購入するページへ進む。 */
export const ReassistArchiveButton: React.FC<EntryProps> = ({
  variant = "outlined",
  ...rest
}) => (
  <ReassistButton
    label={REASSIST_LABELS.archive}
    variant={variant}
    startIcon={<SearchOutlined />}
    {...rest}
  />
);
